import {
	Button,
	ButtonGroup,
	IconButton,
	LuIcon,
	radiusCva,
	shadowCva,
	Text,
	ToggleGroup,
	useProvider,
} from "@overmap-ai/blocks"
import * as RadixDialog from "@radix-ui/react-dialog"
import * as turf from "@turf/turf"
import { cx } from "class-variance-authority"
import type { Geometry } from "geojson"
import { Circle, Diamond, Disc, Dot, Eclipse, Move, Pentagon, Radius, Scale3d, Slash, Square } from "lucide-react"
import { type ComponentProps, memo, useCallback, useMemo, useState } from "react"
import { v4 as uuid } from "uuid"

import { ringCva } from "../../cva"
import {
	CircleDrawer,
	DiamondDrawer,
	DiscDrawer,
	EllipseDrawer,
	GeometryEditor,
	Layer,
	LineStringDrawer,
	LineStringEditor,
	PolygonDrawer,
	PolygonEditor,
	RectangleDrawer,
	SectorDrawer,
	Source,
} from "../../mapbox/components"
import {
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../../mapbox/constants"
import { GeometryDrawMode, GeometryEditMode, MapSlotLayerId } from "../../mapbox/enums"
import { useCurrentMapEvent } from "../../mapbox/hooks"
import { GEOMETRY_DRAW_MODE_INSTRUCTIONS } from "../../mapbox/mappings"
import type { MapEventHandler, UUIDFeature, UUIDFeatureCollection } from "../../mapbox/typings"
import { isLineStrings, isPoints, isPolygons, wrapGeometryInFeature } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { BottomBar } from "../BottomBar/BottomBar"

enum GeometryPickerLayerId {
	CIRCLE = "geometry-picker-circle-layer",
	LINE = "geometry-picker-line-layer",
	FILL = "geometry-picker-fill-layer",
}

const GEOMETRY_DEFAULT_INSTRUCTION =
	"Select a shape tool to draw geometry. Click existing shapes to select and edit them."

interface GeometryPickerOverlayProps extends ComponentProps<typeof RadixDialog.Root> {
	initialGeometry?: Geometry
	onSave?: (geometry: Geometry) => void
	onCancel?: () => void
}

export const GeometryPickerOverlay = memo<GeometryPickerOverlayProps>((props) => {
	const { initialGeometry, onSave, onCancel, ...rest } = props

	const [features, setFeatures] = useState<UUIDFeature[]>(() =>
		initialGeometry
			? turf
					.flatten(initialGeometry)
					.features.map(({ geometry }) => wrapGeometryInFeature(geometry, { uuid: uuid() }))
			: [],
	)
	const { radius } = useProvider()
	const [geometryDrawMode, setGeometryDrawMode] = useState<GeometryDrawMode | "">("")
	const [geometryEditMode, setGeometryEditMode] = useState<GeometryEditMode | "">(GeometryEditMode.EDIT)
	const [selectedFeature, setSelectedFeature] = useState<UUIDFeature | null>(null)

	const handleAddGeometry = useCallback(
		(feature: UUIDFeature) => {
			setFeatures([...features, feature])
		},
		[features, setFeatures],
	)

	const handleDrawModeChange = useCallback((mode: GeometryDrawMode | "") => {
		setGeometryDrawMode(mode)
		setSelectedFeature(null)
		setGeometryEditMode("")
	}, [])

	const handleEditModeChange = useCallback(
		(mode: GeometryEditMode | "") => {
			if (!selectedFeature) return

			switch (mode) {
				case GeometryEditMode.MOVE:
					setGeometryEditMode(GeometryEditMode.MOVE)
					break
				case GeometryEditMode.EDIT:
					setGeometryEditMode(GeometryEditMode.EDIT)
					break
			}
		},
		[selectedFeature],
	)

	const handlePointMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			if (geometryDrawMode !== GeometryDrawMode.POINT) return

			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			handleAddGeometry(wrapGeometryInFeature(point.geometry, { uuid: uuid() }))
			setGeometryDrawMode("")
		},
		[handleAddGeometry, geometryDrawMode],
	)

	const handleGeometryMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			if (geometryDrawMode !== "") return

			const { point, target } = e

			const foundFeatures = target.queryRenderedFeatures(point, {
				layers: [GeometryPickerLayerId.CIRCLE, GeometryPickerLayerId.LINE, GeometryPickerLayerId.FILL],
			})

			if (foundFeatures.length === 0) {
				setSelectedFeature(null)
				return
			}

			const foundFeature = features.find((feature) => feature.properties?.uuid === foundFeatures[0].id)!
			setSelectedFeature(foundFeature.properties.uuid !== selectedFeature?.properties?.uuid ? foundFeature : null)
			setGeometryEditMode(GeometryEditMode.MOVE)
		},
		[features, geometryDrawMode, selectedFeature?.properties.uuid],
	)

	useCurrentMapEvent("click", handlePointMapClick)
	useCurrentMapEvent("click", handleGeometryMapClick)

	const handleSave = useCallback(() => {
		const geometries = features.map(({ geometry }) => geometry)

		let geometry: Geometry | undefined

		switch (geometries.length) {
			case 0:
				geometry = undefined
				break
			case 1:
				geometry = geometries[0]
				break
			default:
				if (isPoints(geometries)) {
					geometry = turf.multiPoint(geometries.map(({ coordinates }) => coordinates)).geometry
				} else if (isLineStrings(geometries)) {
					geometry = turf.multiLineString(geometries.map(({ coordinates }) => coordinates)).geometry
				} else if (isPolygons(geometries)) {
					geometry = turf.multiPolygon(geometries.map(({ coordinates }) => coordinates)).geometry
				} else {
					// @ts-expect-error REASON: we flatten geometries initially, geometries wont contain a GeometryCollection
					geometry = turf.geometryCollection(geometries).geometry
				}
				break
		}

		if (!geometry) return

		onSave?.(geometry)
	}, [features, onSave])

	const handleCancel = useCallback(() => {
		onCancel?.()
	}, [onCancel])

	const handleCopySelectedFeature = useCallback(() => {
		if (!selectedFeature) return

		const geometry = selectedFeature.geometry

		handleAddGeometry(wrapGeometryInFeature(geometry, { uuid: uuid() }))
	}, [handleAddGeometry, selectedFeature])

	const handleDeleteSelectedFeature = useCallback(() => {
		if (!selectedFeature) return

		setFeatures(features.filter((feature) => feature.properties.uuid !== selectedFeature.properties.uuid))
		setSelectedFeature(null)
	}, [features, selectedFeature, setFeatures])

	const handleUpdateFeatureGeometry = useCallback(
		(geometry: Geometry) => {
			if (!selectedFeature) return

			const updatedFeature: UUIDFeature = {
				...selectedFeature,
				geometry: geometry,
			}

			setFeatures([
				...features.filter((feature) => feature.properties.uuid !== selectedFeature.properties.uuid),
				updatedFeature,
			])
			setSelectedFeature(updatedFeature)
		},
		[features, selectedFeature, setFeatures],
	)

	const handleGeometryDraw = useCallback(
		(geometry: Geometry) => {
			handleAddGeometry(wrapGeometryInFeature(geometry, { uuid: uuid() }))
			setGeometryDrawMode("")
		},
		[handleAddGeometry],
	)

	const geoJson = useMemo<UUIDFeatureCollection>(() => {
		return turf.featureCollection(features)
	}, [features])

	return (
		<RadixDialog.Root {...rest} modal={false}>
			<Source type="geojson" data={geoJson} promoteId="uuid">
				<Layer
					id={GeometryPickerLayerId.CIRCLE}
					type="circle"
					paint={DRAW_CIRCLE_LAYER_PAINT}
					filter={POINT_GEOMETRY_TYPE_FILTER}
					beforeId={MapSlotLayerId.DRAW}
				/>
				<Layer
					id={GeometryPickerLayerId.LINE}
					type="line"
					paint={DRAW_LINE_LAYER_PAINT}
					filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
					beforeId={MapSlotLayerId.DRAW}
				/>
				<Layer
					id={GeometryPickerLayerId.FILL}
					type="fill"
					paint={DRAW_FILL_LAYER_PAINT}
					filter={POLYGON_GEOMETRY_TYPE_FILTER}
					beforeId={MapSlotLayerId.DRAW}
				/>
			</Source>

			{geometryDrawMode === GeometryDrawMode.LINE && <LineStringDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.POLYGON && <PolygonDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.RECTANGLE && <RectangleDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.CIRCLE && <CircleDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.SECTOR && <SectorDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.ELLIPSE && <EllipseDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.DISC && <DiscDrawer onComplete={handleGeometryDraw} />}
			{geometryDrawMode === GeometryDrawMode.DIAMOND && <DiamondDrawer onComplete={handleGeometryDraw} />}

			{!!selectedFeature && geometryEditMode === GeometryEditMode.MOVE && (
				<GeometryEditor
					key={selectedFeature.properties.uuid}
					geometry={selectedFeature.geometry}
					onGeometryChange={handleUpdateFeatureGeometry}
				/>
			)}

			{!!selectedFeature && geometryEditMode === GeometryEditMode.EDIT ? (
				<>
					{selectedFeature.geometry.type === "Polygon" ? (
						<PolygonEditor
							polygon={selectedFeature.geometry}
							onPolygonChange={handleUpdateFeatureGeometry}
						/>
					) : null}
					{selectedFeature.geometry.type === "LineString" ? (
						<LineStringEditor
							lineString={selectedFeature.geometry}
							onLineStringChange={handleUpdateFeatureGeometry}
						/>
					) : null}
				</>
			) : null}

			<RadixDialog.Content className="pointer-events-none fixed inset-0">
				<div className="pointer-events-auto absolute right-4 top-1/2 flex -translate-y-1/2 flex-col gap-4">
					<ButtonGroup
						className={cx(
							"bg-(--color-background) flex flex-col",
							ringCva({ alpha: false }),
							radiusCva({ radius }),
							shadowCva({ size: "md" }),
						)}
						variant="soft"
						accentColor="base"
					>
						<ToggleGroup.Root
							className="flex flex-col"
							type="single"
							variant="soft"
							value={geometryDrawMode}
							accentColor="base"
							onValueChange={handleDrawModeChange}
						>
							<ToggleGroup.IconItem className="rounded-b-none" value={GeometryDrawMode.POINT}>
								<LuIcon icon={Dot} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.LINE}>
								<LuIcon icon={Slash} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.POLYGON}>
								<LuIcon icon={Pentagon} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.RECTANGLE}>
								<LuIcon icon={Square} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.CIRCLE}>
								<LuIcon icon={Circle} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.SECTOR}>
								<LuIcon icon={Radius} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.ELLIPSE}>
								<LuIcon icon={Eclipse} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-none" value={GeometryDrawMode.DISC}>
								<LuIcon icon={Disc} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-t-none" value={GeometryDrawMode.DIAMOND}>
								<LuIcon icon={Diamond} />
							</ToggleGroup.IconItem>
						</ToggleGroup.Root>
					</ButtonGroup>

					{!!selectedFeature &&
					(selectedFeature.geometry.type === "Polygon" || selectedFeature.geometry.type === "LineString") ? (
						<>
							<ButtonGroup
								className={cx(
									"bg-(--color-background) flex flex-col",
									ringCva({ alpha: false }),
									radiusCva({ radius }),
									shadowCva({ size: "md" }),
								)}
								accentColor="base"
								variant="soft"
							>
								<ToggleGroup.Root
									className="flex flex-col"
									type="single"
									variant="soft"
									value={geometryEditMode}
									accentColor="base"
									onValueChange={handleEditModeChange}
								>
									<ToggleGroup.IconItem className="rounded-b-none" value={GeometryEditMode.MOVE}>
										<LuIcon icon={Move} />
									</ToggleGroup.IconItem>
									<ToggleGroup.IconItem className="rounded-t-none" value={GeometryEditMode.EDIT}>
										<LuIcon icon={Scale3d} />
									</ToggleGroup.IconItem>
								</ToggleGroup.Root>
							</ButtonGroup>

							<ButtonGroup
								className={cx(
									"bg-(--color-background) flex flex-col",
									ringCva({ alpha: false }),
									radiusCva({ radius }),
									shadowCva({ size: "md" }),
								)}
								accentColor="base"
								variant="soft"
							>
								<IconButton className="rounded-b-none" onClick={handleCopySelectedFeature}>
									<LuIcon icon={modelIconMapping.copy} />
								</IconButton>

								<IconButton className="rounded-t-none" onClick={handleDeleteSelectedFeature}>
									<LuIcon icon={modelIconMapping.delete} />
								</IconButton>
							</ButtonGroup>
						</>
					) : null}
				</div>

				<BottomBar className="flex flex-col gap-2">
					<Text size="sm" accentColor="base" align="center">
						{geometryDrawMode
							? GEOMETRY_DRAW_MODE_INSTRUCTIONS[geometryDrawMode]
							: GEOMETRY_DEFAULT_INSTRUCTION}
					</Text>
					<ButtonGroup className="pointer-events-auto flex w-full gap-2" size="sm">
						<Button className="grow basis-1" variant="soft" accentColor="base" onClick={handleCancel}>
							<LuIcon icon={modelIconMapping.close} />
							Cancel
						</Button>
						<Button className="grow basis-1" variant="surface" onClick={handleSave}>
							<LuIcon icon={modelIconMapping.check} />
							Save
						</Button>
					</ButtonGroup>
				</BottomBar>
			</RadixDialog.Content>
		</RadixDialog.Root>
	)
})
GeometryPickerOverlay.displayName = "GeometryPickerOverlay"
